import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import type { Role } from '@pgway/shared';
import { useAuth } from '../../contexts/AuthContext';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

interface RoleLayoutProps {
  requiredRole: Role;
}

export function RoleLayout({ requiredRole }: RoleLayoutProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-gray-400 dark:text-gray-500">…</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.mustChangePassword) {
    return <Navigate to="/set-password" replace />;
  }

  if (user.role !== requiredRole) {
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 dark:bg-gray-900">
      <Sidebar
        role={requiredRole}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Floating gradient header band — TopBar lives transparent over it */}
        <header className="mx-3 mt-3 rounded-2xl bg-gradient-primary px-4 py-3 shadow-raised dark:bg-gradient-primary-dark sm:mx-4 sm:mt-4 sm:px-6 sm:py-4">
          <TopBar onMenuClick={() => setMobileNavOpen(true)} />
        </header>
        <main className="scrollbar-thin flex-1 overflow-y-auto px-3 pb-6 pt-4 sm:px-4 sm:pb-8 sm:pt-6">
          <div className="mx-auto max-w-screen-2xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
